<template>
    <div>
        <div class="row">
            <div class="col-lg-12 mb-3">
                <b-breadcrumb :items="breadcrumbItems" class="breadcrumb1 breadcrumbCustom border"/>
                <b-card :header="$t('general.scaffolding.parts.title')" header-tag="h3" class="bg-default-card">
                    <data-table
                        v-if="tableLoaded"
                        :exportable="this.$store.getters.hasPermission(['project_scaffolding_part_list'], ['read'])" title=""
                        :importable="true"
                        title=""
                        :rows="getData.rowData"
                        :columns="columnData"
                        :perPage="perPage"
                        :rowsCount="rowsCount"
                        :currentPage="currentPage"
                        :pageCount="pageCount"
                        @rowClickDelete="onDeleteRow"
                        @rowClickEdit="onEditRow"
                        @changePage="changePage"
                        @searchEvent="searchEvent"
                        @orderByEvent="orderByEvent"
                        @exportButtonClick="onExportButton"
                    >
                    </data-table>

                    <b-btn class="btn btn-dark mt-2 mr-2" @click="returnToIso">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i> {{$t('general.buttons.return_to.return_to_scaffolding')}}
                    </b-btn>

                    <b-btn class="btn btn-default mt-2 mr-2" @click="onAddRow" v-if="this.$store.getters.hasPermission(['project_scaffolding_part_list'], ['create'])">
                        <i class="fa fa-plus-circle mr-1"></i> {{ $t('general.part.create_button') }}
                    </b-btn>

                    <span v-if="this.$store.getters.hasPermission(['project_scaffolding'], ['create', 'update'])">
                        <label v-if="!this.$store.state.ajax_in_progress" class="btn btn-primary mt-3 mr-1" @click="$refs.importExcel.value = ''">
                            <span><i class="fa fa-upload mr-1"></i> {{ $t('general.isometric.parts.import_button') }}</span>
                            <input type="file" name="file" style="display:none" ref="importExcel" @change="onImportButton($event.target.name, $event.target.files)" accept="application/*"/>
                        </label>

                        <label v-else class="btn btn-primary mt-3 disabled">
                            <span><i class="fa fa-upload mr-1"></i> {{ $t('general.ajax_in_progress_please_wait') }}</span>
                        </label>
                    </span>
                </b-card>
            </div>
        </div>

        <b-modal ref="saveModal" :ok-title="$t('misc.ok')" :cancel-title="$t('misc.cancel')" :title="actionTitle" size="lg" @ok="saveAction" :ok-disabled="$store.state.ajax_in_progress || (row && !$store.getters.hasPermission(['project_scaffolding_part_list'], ['update']))">
            <div class="row mb-3">
                <div class="col-lg-2">
                    <label>{{ $t('general.isometric.parts.search_price') }}</label>
                </div>
                <div class="col-lg-10">
                    <multiselect v-model="selectedPrice"
                        :options="currentPrice"
                        :multiple="false"
                        :clear-on-select="true"
                        :hide-selected="false"
                        :loading="isPriceLoading"
                        :preserve-search="true"
                        placeholder=""
                        label="info" track-by="id"
                        :selectLabel="''"
                        @search-change="onPriceSearch"
                        @input="updatePrice"
                    >   
                        <template slot="clear" slot-scope="props">
                            <div 
                                v-if="selectedPrice != null"
                                class="multiselect__clear" 
                                @mousedown.prevent.stop="clearSelectedPrice"
                            >
                            </div>
                        </template>
                        <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                        <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                    </multiselect>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-6">
                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.part_number') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="text" maxlength="50" class="form-control" v-model="model.part_number">
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.quantity') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.quantity">
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.part_measureunit') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <select class="form-control" v-model="model.measure_unit">
                                <option v-for="measure_unit in measure_units" :value="measure_unit.code" :key="measure_unit.code">{{ measure_unit.name }}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="col-lg-6">
                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.length') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.length">
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.width') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.width">
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.height') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.height">
                        </div>
                    </div>

                    <div class="row mb-3" v-if="row && row.m3 != 0">
                        <div class="col-lg-4">
                            <label>{{ $t('table.m3') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.m3" disabled>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-12">
                    <label>{{ $t('table.description') }}</label>
                    <textarea rows="6" maxlength="5000" class="form-control" v-model="model.description"></textarea>
                </div>
            </div>
        </b-modal>

        <confirm-dialog ref="deleteModal" :title="actionTitle" @buttonClick="deleteAction">
            {{ $t('messages.delete') }}
        </confirm-dialog>
    </div>
</template>

<script>
import Vue from 'vue';
import http from '@/libs/http';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import miniToastr from 'mini-toastr';
import Multiselect from 'vue-multiselect';

export default {
    name: 'scaffolding_parts',
    components: {
        DataTable,
        ConfirmDialog,
        notificationIcons,
        Multiselect,
    },
    data() {
        return {
            breadcrumbItems: [
                {
                    text: Vue.i18n.translate('general.dashboard.title'),
                    to: '/',
                },
                {
                    text: Vue.i18n.translate('general.projects.title_modal') + ': ' + this.$store.state.current_project.name,
                    to: '/project/'+this.$route.params.id,
                },
                {
                    text: Vue.i18n.translate('general.construction.title'),
                    active: true
                },
                {
                    text: Vue.i18n.translate('general.construction.scaffolding.title'),
                    active: true
                },
                {
                    text: Vue.i18n.translate('general.parts.title'),
                    active: true
                }
            ],
            tableName: 'scaffolding_part',
            tableNameTranslated: Vue.i18n.translate('general.scaffolding.parts.modal_title'),
            actionTitle: '',
            row: null,
            tableLoaded: false,
            perPage: 0,
            rowsCount: 0,
            currentPage: 1,
            sortType: 'asc',
            sortBy: 'id',
            searchInput: '',

            selectedPrice: null,
            currentPrice: [],
            isPriceLoading: false,

            getData: {
                rowData: [],
            },

            model: {
                part_number: null,
                description: null,
                quantity: null,
                measure_unit: null,
                length: null,
                width: null,
                height: null,
                m3: null,
            },

            columnData: [
                {
                    label: Vue.i18n.translate('misc.actions'),
                    width: '90px',
                    field: '',
                    showDeleteButton: true,
                    showEditButton: true,
                },
                {
                    label: Vue.i18n.translate('table.part_number'),
                    field: 'part_number',
                    numeric: false,
                    html: false,
                    sortable: true,
                },
                {
                    label: Vue.i18n.translate('table.measure_unit'),
                    field: 'measure_unitV',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.part_description'),
                    field: 'description',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.quantity'),
                    field: 'quantity',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.length'),
                    field: 'length',
                    numeric: true,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.width'),
                    field: 'width',
                    numeric: true,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.height'),
                    field: 'height',
                    numeric: true,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.m3'),
                    field: 'm3',
                    numeric: true,
                    html: false,
                }
            ]
        }
    },
    mounted () {
        this.onLoadTable();
    },
    computed: {
        currencies: {
            get: function () {
                let currencies = this.$store.getters.getCurrencies;
                return currencies;
            }
        },
        measure_units: {
            get: function () {
                let measure_units = this.$store.getters.getMeasureUnits;
                return measure_units;
            }
        }
    },
    watch: {
        measure_units: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        }
    },
    methods: {
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        orderByEvent (column, sort_type) {
            this.sortType = sort_type;
            this.sortBy = column.field;
            this.onLoadTable();
        },
        searchEvent (searchInput) {
            this.searchInput = searchInput;
            this.onLoadTable();
        },
        changePage (page) {
            this.currentPage = page;
            this.onLoadTable();
        },
        onLoadTable () {
            let data = http.route(this.tableName + '?project_id='+this.$route.params.id+'&filters[scaffolding_id]=' + this.$route.params.id2 + '&search=' + this.searchInput + '&page=' + this.currentPage + '&sort_by=' + this.sortBy + '&sort_order=' + this.sortType).get();
            data.then(response => {
                if (!response.isError) {
                    this.perPage = response.pagination.limit;
                    this.rowsCount = response.pagination.totalEntries;
                    this.currentPage = response.pagination.currentPage;
                    this.pageCount = response.pagination.totalPages;
                    var data = response.data;
                    data.forEach(field => {
                        if (typeof this.measure_units[field.measure_unit] != 'undefined') {
                            field.measure_unitV = this.measure_units[field.measure_unit].name;
                        }
                    });
                    this.getData.rowData = data;
                    this.tableLoaded = true;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onAddRow () {
            this.actionTitle = Vue.i18n.translate('modal.general.create') + ' ' + this.tableNameTranslated;
            this.row                = null;
            this.model.part_number  = null;
            this.model.description  = null;
            this.model.quantity     = null;
            this.model.measure_unit = null;
            this.model.length       = null;
            this.model.width        = null;
            this.model.height       = null;
            this.model.m3           = null;

            this.clearSearches();
            this.$refs.saveModal.show();
        },
        onEditRow (row) {
            this.actionTitle = Vue.i18n.translate('modal.general.edit') + ' ' + this.tableNameTranslated;
            this.row                = row;
            this.model.part_number  = row.part_number;
            this.model.description  = row.description;
            this.model.quantity     = row.quantity;
            this.model.measure_unit = row.measure_unit;
            this.model.length       = row.length;
            this.model.width        = row.width;
            this.model.height       = row.height;
            this.model.m3           = row.m3;

            this.clearSearches();
            this.$refs.saveModal.show();
        },
        onDeleteRow (row, index) {
            this.actionTitle = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated;
            this.row = row;
            this.$refs.deleteModal.show();
        },
        saveAction (e) {
            e.preventDefault();

            let put_data = {
                project_id: this.$route.params.id,
                scaffolding_id: this.$route.params.id2,
                part_number: this.model.part_number,
                description: this.model.description,
                quantity: this.model.quantity,
                measure_unit: this.model.measure_unit,
                length: this.model.length,
                width: this.model.width,
                height: this.model.height
            };

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            if (this.row) {
                http.route(this.tableName + '/' + this.row.id).patch(put_data, true).then(response => {
                    if (response.isError == false) {
                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                        this.$refs.saveModal.hide();
                    } else {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    }
                });
            } else {
                http.route(this.tableName).post(put_data, true).then(response => {
                    if (response.isError == false) {
                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.created'));
                        this.$refs.saveModal.hide();
                    } else {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    }
                });
            }
        },
        deleteAction (button) {
            if (button == 1) {
                let data = http.route(this.tableName + '/' + this.row.id).delete();
                data.then(response => {
                    if (!response.isError) {
                        if (this.currentPage > 1 && this.getData.rowData.length == 1) {
                            this.currentPage = this.currentPage - 1;
                        }

                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                    } else {
                        this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                    }
                });
            }
            this.$refs.deleteModal.hide();
        },
        onExportButton() {
            http.route(this.tableName).exportExcel({
                export: 1,
                project_id: this.$route.params.id,
                scaffolding_id: this.$route.params.id2,
                search: this.searchInput,
                sort_by: this.sortBy,
                sort_order: this.sortType
            }, 'scaffolding_parts.xlsx').then(response => {
                if (typeof response != 'undefined') {
                    this.errorMsg(Vue.i18n.translate('errors.error_export'));
                } else {
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.exported'));
                }
            });
        },
        onImportButton(fieldName, fileList) {
            const formData = new FormData();

            if (!fileList.length) return;

            formData.append('project_id', this.$route.params.id);
            formData.append('scaffolding_id', this.$route.params.id2);

            Array
            .from(Array(fileList.length).keys())
            .map(x => {
                formData.append(fieldName, fileList[x], fileList[x].name);
            });

            http.route(this.tableName + '/import').importExcel(formData)
            .then(response => {
                if (response.isError) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.onLoadTable();
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.imported'));
                }
            });
        },
        returnToIso() {
            this.$router.push('/project/'+this.$route.params.id+'/construction/scaffolding');
        },
        clearSearches () {
            this.selectedPrice = null;
            this.currentPrice = [];
        },
        onPriceSearch(search) {
            if (search == '') {
                this.currentPrice = [];
                
                return false;
            }

            this.isPriceLoading = true;
            this.searchPrice(search, this);
        },
        searchPrice: _.debounce((search, vm) => {
            let data = http.route('project_scaffolding_price_list?project_id='+ vm.$route.params.id +'&get_all=1&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentPrice = response.data;
                }
                vm.isPriceLoading = false;
            });
        }, 500),
        updatePrice(price) {
            this.model.part_number  = price.scaffolding_part_no;
            this.model.description  = price.scaffolding_part_description;
            this.model.measure_unit = price.measureunit;
            this.model.length       = price.scaffolding_part_length;
            this.model.width        = price.scaffolding_part_width;
            this.model.height       = price.scaffolding_part_height;
            if (this.row != null) {
                this.row.m3 = 0;
            }
        },
        clearSelectedPrice () {
            this.selectedPrice = null;
        },
    }
}
</script>

<style scoped>
	select {
		-webkit-appearance:menulist-button;
		-moz-appearance:menulist-button;
	}
    .multiselect-custom {
        width: 300px !important;
        height: 40px !important;
    }
    .multiselect__input:focus {
        width: 100% !important;
    }
    select {
        -webkit-appearance:menulist-button;
        -moz-appearance:menulist-button;
    }
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>



// WEBPACK FOOTER //
// src/components/pages/project/construction/scaffolding/scaffolding_parts.vue